import React, { useMemo } from 'react';
import { compose, path, pick, pipe, prop } from 'ramda';
import { pure } from 'recompose';

import { FavoriteIcon } from '@core/js/components/common/Game/FavoriteIcon';
import { Image } from '@core/js/components/common/Image';
import { DemoButton, PlayButton } from '@core/js/components/main/Games/Buttons';
import { usePortletConfig, usePortletSelector } from '@core/js/components/system/Portlet';

import { GameInfoBtn } from '@interlayer/js/components/common/Game/sun/components/GameInfoBtn';

import { withTranslations } from '@core/js/components/hocs/withTranslations';
import { useGameImageUrls, useLiveGameConfig } from '@core/js/hooks/games';
import { useGameConfigurationSelector, useUserSelector } from '@core/js/store/hooks';

import style from './index.scss';

export const Description = withTranslations(({ code, t }) => {
    const tKey = `playtech.mobile.system.gamedescription.${code}`;
    const MAX_LENGTH = 145;
    let value = t(tKey);

    value = value.includes(tKey)
        ? ''
        : value.length > MAX_LENGTH ? `${value.substring(0, MAX_LENGTH)}...` : value;

    return value && <p className={style.desc}>{value}</p>;
});

export const HoveredGame = ({
    t,
    code,
    gameImageUrl,
    setGameInfoRef,
    showFavoriteIcon
}) => {
    const gameFeed = usePortletSelector(prop('feedName'));
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { name, demoModeSupport, liveLaunchAlias } = useGameConfigurationSelector(pipe(
        path(['games', code]),
        pick(['name', 'demoModeSupport', 'liveLaunchAlias'])
    ));
    const { disableDemoWhenLoggedIn } = usePortletConfig();
    const showDemoButton = !(
        demoModeSupport === 'unavailable'
        || isLoggedIn && (demoModeSupport === 'guest' || disableDemoWhenLoggedIn)
    );

    const liveGameConfig = useLiveGameConfig(liveLaunchAlias);
    const isLiveGame = liveGameConfig?.launchAlias;
    const { dynamicImageUrl, limits, openingDateTime, isScheduled } = liveGameConfig || {};
    const isScheduledLiveGame = isLiveGame && isScheduled;
    const imageUrls = useGameImageUrls({ code, dynamicImageUrl, isLiveGame });
    const additionalAssets = useGameConfigurationSelector(path(['games', code, 'additionalAssets']));
    const additionalAssetsImg = useMemo(() =>
        additionalAssets?.find((obj) => obj.name === gameFeed),
        [additionalAssets, gameFeed]
    );

    return (
        <div ref={setGameInfoRef} className={style.gameInfoContainer}>
            <div className={style.flip}>
                <div className={style.front}>
                    <Image
                        alt={name}
                        className={style.image}
                        src={additionalAssetsImg?.url || gameImageUrl || imageUrls}
                    />
                    <div className={`${isScheduledLiveGame ? style.scheduledLiveGameBackground : style.gameBackground}`} />
                    <div className={style.gameContent}>
                        <GameInfoBtn code={code} gameFeed={gameFeed} />

                        <h2 className={style.title}>{name}</h2>
                        <div className={style.buttons}>
                            {showDemoButton && !isLiveGame && (
                                <DemoButton code={code} name={name} isHoverBtn={true} />
                            )}
                            {isScheduledLiveGame
                                ? (
                                    <div className={style.scheduledLiveGameInfo}>
                                        {openingDateTime
                                            ? (
                                                <>
                                                    <span>{t('playtech.mobile.system.liveGame.startsAt')}</span>
                                                    <span>{openingDateTime}</span>
                                                </>
                                            )
                                            : <span>{t('playtech.mobile.system.liveGame.startsSoon')}</span>
                                        }
                                    </div>
                                )
                                : <PlayButton code={code} name={name} isHoverBtn={true} />
                            }
                        </div>
                        <Description code={code} />
                        <div className={style.bottomIcons}>
                            {isLiveGame && limits && <div className={style.liveLimits}>{limits}</div>}
                            {showFavoriteIcon && (
                                <FavoriteIcon
                                    code={code}
                                    gameFeed={gameFeed}
                                    className={style.favorite}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default compose(
    pure,
    withTranslations
)(HoveredGame);
